var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { cssColorValue, desktopBallad, desktopCelloCanon, HorizontalRule, IconArrowTopRight, IconChevronDown, IconChevronUp, mobileBallad, mobileCello, PopoverNavigation, PopoverNavigationItem, screenXsMin, spacer12, spacer20, spacer24, spacer32, spacer4, spacer40, spacer64, } from '@spotify-internal/encore-web';
import React, { useState } from 'react';
import styled from 'styled-components';
import { ButtonTertiaryLink } from '../Link/Link';
import { NavBarLanguageItem } from './Language';
import { NavBarSideMenuLink, useNavBarActions } from './hooks/useNavBarActions';
import { useS4AMastheadContext } from '../S4AMastheadProvider';
import { isMastheadLink, isMastheadLinkList, isMastheadLinkListItem, } from '../../utils/contentful';
import { isNotUndefined } from '../../lib/isNotUndefined';
import useDropdown, { DropdownContext } from '../../hooks/useDropdown';
import { trackingAttributes } from '../../lib/buildTrackingAttributes';
const StyledPopoverNavigation = styled(PopoverNavigation) `
  background: ${cssColorValue('backgroundBase')};
  bottom: 0;
  display: flex;
  flex-direction: column;
  overflow: auto;
  padding: ${spacer64} ${spacer20} ${spacer20};
  position: fixed;
  inset-inline-end: 0;
  top: 0;
  width: calc(${spacer24} * 10 + ${spacer40});

  &&:focus {
    outline: none;
  }
`;
const NavList = styled.ul `
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  gap: ${spacer20};
  list-style-type: none;
  margin-bottom: ${spacer32};
  margin-top: 0;
  padding: 0;
`;
const MenuSeparator = styled(HorizontalRule) `
  border-color: ${cssColorValue('essentialBase')};
  border-width: 1px;
  margin: ${spacer12} 0;
  width: ${spacer40};
`;
export const NavBarSideMenuDropdownBtn = styled(ButtonTertiaryLink) `
  && {
    border-radius: 0;
    color: ${cssColorValue('textBase')};
    text-decoration: none;
    ${desktopCelloCanon};

    &&:active,
    &&:hover,
    &&:focus {
      color: ${cssColorValue('textBase')};
      opacity: 1;
      text-decoration: none;
      transform: none;
    }

    &:focus {
      outline: 5px auto -webkit-focus-ring-color;
      outline-offset: ${spacer4};
    }

    &::after {
      display: none;
    }

    @media (max-width: ${screenXsMin}) {
      ${mobileCello}
    }
  }
`;
const StyledIconChevronDown = styled(IconChevronDown) `
  @media (max-width: ${screenXsMin}) {
    transform: translateY(2px);
  }
`;
const StyledIconChevronUp = styled(IconChevronUp) `
  @media (max-width: ${screenXsMin}) {
    transform: translateY(4px);
  }
`;
const SubMenuList = styled(NavList) `
  margin-bottom: 0;
  margin-inline-start: ${spacer20};
  margin-top: ${spacer20};
`;
const SubMenuLink = styled(ButtonTertiaryLink) `
  color: ${cssColorValue('textSubdued')};
  cursor: pointer;
  justify-content: flex-start;
  ${desktopBallad};

  &:active,
  &:hover,
  &:focus {
    color: ${cssColorValue('textSubdued')};
    transform: none;
  }

  [dir='rtl'] & * {
    direction: rtl;
    & > svg {
      transform: rotateY(180deg);
    }
  }

  & > * {
    margin-inline-start: ${spacer4};
    transform: translateY(1px);
  }

  @media (max-width: ${screenXsMin}) {
    ${mobileBallad}
  }
`;
const StyledPopoverNavigationItem = styled(PopoverNavigationItem) `
  ${desktopCelloCanon};

  @media (max-width: ${screenXsMin}) {
    ${mobileCello}
  }
`;
const SideMenuAccordion = ({ linkListEntry, mastheadData, closeBackdrop, }) => {
    const { trackCat } = useS4AMastheadContext();
    const { handlers, open } = useDropdown(linkListEntry.sys.id, true);
    const links = linkListEntry.fields.entries
        ? linkListEntry.fields.entries
            .filter(isMastheadLinkListItem)
            .map(linkListItem => {
            const link = linkListItem.fields.mastheadLink;
            return {
                track: trackingAttributes(trackCat, link.fields.trackingId),
                href: link.fields.href,
                linkText: link.fields.title,
                level: linkListItem === null || linkListItem === void 0 ? void 0 : linkListItem.fields.nestingLevel,
                openInNewWindow: link.fields.target === 'blank',
            };
        })
        : [];
    return (React.createElement(React.Fragment, null,
        React.createElement(NavBarSideMenuDropdownBtn, { href: "", iconTrailing: () => open ? (React.createElement(StyledIconChevronUp, { size: "medium", "aria-label": mastheadData.fields.dropdownMenuLabelClose })) : (React.createElement(StyledIconChevronDown, { size: "medium", "aria-label": mastheadData.fields.dropdownMenuLabelOpen })), track: ['', ''], onKeyDown: handlers.handleOnKeyDown, onMouseDown: handlers.handleOnMouseDown }, linkListEntry.fields.label),
        open && (React.createElement(SubMenuList, null, links.map((_a, i) => {
            var { linkText, openInNewWindow, href, level, track } = _a, rest = __rest(_a, ["linkText", "openInNewWindow", "href", "level", "track"]);
            return (React.createElement("li", { key: i },
                React.createElement(SubMenuLink, Object.assign({ onClick: closeBackdrop, openInNewWindow: openInNewWindow, href: href, track: track, iconTrailing: openInNewWindow
                        ? () => React.createElement(IconArrowTopRight, { size: "small" })
                        : undefined }, rest), linkText)));
        })))));
};
export const NavBarSideMenu = ({ language, userState, mastheadData, closeBackdrop, }) => {
    const { trackCat } = useS4AMastheadContext();
    const headerEntry = mastheadData.fields.header;
    const languageSelectionEntry = mastheadData.fields
        .languageSelection;
    const [activeDropdown, setActiveDropdown] = useState(null);
    const { loginAction, logoutAction, claimAction, dashboardAction } = useNavBarActions({
        container: 'sidemenu',
        userState,
        headerEntry,
        closeBackdrop,
    });
    const { dir } = language;
    return (React.createElement(StyledPopoverNavigation, { as: "nav", dir: dir, role: "dialog", tabIndex: 0, "aria-label": headerEntry.fields.sideMenuPopoverAriaLabel },
        React.createElement(NavList, null,
            [claimAction, dashboardAction, logoutAction, loginAction]
                .filter((el) => Boolean(el))
                .map(action => (React.createElement(StyledPopoverNavigationItem, { onClick: closeBackdrop, key: action.key }, action))),
            React.createElement(StyledPopoverNavigationItem, null,
                React.createElement(MenuSeparator, null)),
            headerEntry.fields
                .centerNav.filter(isNotUndefined)
                .filter(item => isMastheadLink(item) || isMastheadLinkList(item))
                .map(item => {
                let content;
                if (isMastheadLink(item)) {
                    content = (React.createElement(NavBarSideMenuLink, { track: [trackCat, item.fields.trackingId], href: item.fields.href, openInNewWindow: item.fields.target === 'blank', onClick: closeBackdrop }, item.fields.title));
                }
                else if (isMastheadLinkList(item)) {
                    content = (React.createElement(DropdownContext.Provider, { value: { activeDropdown, setActiveDropdown } },
                        React.createElement(SideMenuAccordion, { mastheadData: mastheadData, linkListEntry: item, closeBackdrop: closeBackdrop })));
                }
                return (React.createElement(StyledPopoverNavigationItem, { key: item.sys.id }, content));
            })),
        React.createElement("div", null,
            React.createElement(NavBarLanguageItem, { container: "sidemenu", language: language, languageSelectionEntry: languageSelectionEntry, closeBackdrop: closeBackdrop }))));
};
//# sourceMappingURL=SideMenu.js.map